/* ============================================================
   The board pack.

   Thirteen tools already print. Printing is not the same as producing a
   board paper, and the gap is not formatting — it is what a director
   needs in order to disagree with you.

   A printed screen leads with findings and buries the inputs. A board
   paper does the reverse, because the argument in the room is almost
   never about the arithmetic. It is "where did 28 tonnes come from",
   "is that this year's rate", and "what did you not have when you ran
   this". Answer those on the page and the meeting is about the
   decision. Leave them off and the meeting is about the model.

   So this module composes three things the screen never showed:

   1. THE ASSUMPTIONS REGISTER. Every input, including the ones nobody
      supplied. This is the most-read page of any board paper and the
      product had no equivalent. Absent renders as "Not supplied" and
      never as zero — the same rule the engine and the share encoder
      both keep, and the one that matters most here, because a board
      reading "CIF: 0" concludes duty was considered and found to be
      nil, when in fact it was never entered.

   2. THE BASIS OF THE BENCHMARK. The bands carry their derivation,
      their payload assumption, when they were measured and when they
      were last checked. All of it sat in benchmarks.js and none of it
      reached the paper. A comparison against an unnamed band is an
      opinion with a decimal point.

   3. A DOCUMENT REFERENCE that is derived from the inputs rather than
      from the clock. Two prints of the same run carry the same
      reference; change one figure and it changes. That makes a paper
      citable in minutes ("the analysis at KLA-7F3C-21") and makes a
      substituted version detectable, which a timestamp cannot do.

   What this module deliberately does NOT do is restate the findings.
   They are already on the page being printed, they are written to be
   read, and duplicating them into a summary invites the two copies to
   drift. The preamble sets up the findings; it does not replace them.
   ============================================================ */

/* Every field the corridor run accepts, in the order a reader would
   look for them: what was carried, what it cost, what it earns, then
   the customs and cash figures that only some runs have. The register
   walks THIS list, not the keys present on the input — which is the
   whole point. A field missing from the object still gets a row.

   Each tool declares its own list. A shared one would either grow to
   the union of every tool's inputs — printing corridor distance on a
   lender's assessment — or shrink to their intersection, which is
   nothing. What is shared is the RULE: enumerate what the tool accepts,
   not what arrived. */
export const CORRIDOR_RUN_REGISTER = [
  { key: 'corridorKey', label: 'Corridor', kind: 'text' },
  { key: 'mode', label: 'Mode', kind: 'text' },
  { key: 'distance', label: 'Distance', unit: 'km' },
  { key: 'payload', label: 'Payload', unit: 't' },
  { key: 'containers', label: 'Containers', unit: '' },
  { key: 'tripCost', label: 'Cost of the run', unit: 'USD' },
  { key: 'tripRevenue', label: 'Revenue for the run', unit: 'USD' },
  { key: 'cif', label: 'Customs value (CIF)', unit: 'USD' },
  { key: 'dutyBandId', label: 'Duty band', kind: 'text' },
  { key: 'freeDays', label: 'Free days', unit: 'days' },
  { key: 'daysHeld', label: 'Days held', unit: 'days' },
  { key: 'dso', label: 'Debtor days', unit: 'days' },
  { key: 'fuelLitres', label: 'Fuel burned', unit: 'litres' },
  { key: 'operatorType', label: 'Operator type', kind: 'text' }
];

/* The lender's version. Different fields, same rule — and one extra
   kind: a tick box is a claim about the business ("audited accounts
   exist"), and an unticked box is a claim too. It must print as "No",
   never as absent, because a lender reading a blank concludes the
   question was not asked. */
export const FINANCEABILITY_REGISTER = [
  { key: 'annualRevenue', label: 'Annual revenue', unit: 'USD' },
  { key: 'ebitda', label: 'Annual EBITDA', unit: 'USD' },
  { key: 'annualDebtService', label: 'Annual debt service', unit: 'USD' },
  { key: 'topClientShare', label: 'Largest client share', unit: '%' },
  { key: 'dso', label: 'Debtor days', unit: 'days' },
  { key: 'dpo', label: 'Creditor days', unit: 'days' },
  { key: 'dutyFloatDays', label: 'Duty float', unit: 'days' },
  { key: 'fleetAge', label: 'Average fleet age', unit: 'years' },
  { key: 'auditedAccounts', label: 'Audited accounts', kind: 'bool' },
  { key: 'managementAccounts', label: 'Monthly management accounts', kind: 'bool' },
  { key: 'assetRegister', label: 'Asset register', kind: 'bool' },
  { key: 'complianceCurrent', label: 'Compliance current', kind: 'bool' }
];

/* A figure the operator typed and a figure the tool filled in are
   different kinds of claim, and a board is entitled to know which is
   which. Distance is the one that routinely arrives without anyone
   choosing it — selecting a corridor populates it from the benchmark —
   so it is marked when it still matches. */
function originOf(key, input, benchmark, field) {
  if (key === 'distance' && benchmark && input.distance === benchmark.distance) {
    return 'From the corridor record';
  }
  if (key === 'containers' && input.containers === 1) return 'Default';
  if (key === 'dutyBandId') return 'Selected';
  // A tick box was not typed; saying "Entered" of an untouched checkbox
  // would overstate how deliberate a "No" was.
  if (field?.kind === 'bool') return input[key] ? 'Declared' : 'Not declared';
  return 'Entered';
}

export const NOT_SUPPLIED = 'Not supplied';

/**
 * The assumptions register: one row per accepted input, present or not.
 * @returns {Array<{label: string, value: string, origin: string, supplied: boolean}>}
 */
export function assumptionsRegister(input, benchmark, register = CORRIDOR_RUN_REGISTER) {
  return register.map((field) => {
    const raw = input?.[field.key];

    /* A tick box is never absent. False is an answer — "there are no
       audited accounts" — and printing it as Not supplied would tell a
       lender the question was never put, which is a different and much
       more forgiving statement than the truth. */
    const supplied =
      field.kind === 'bool' ? typeof raw === 'boolean' : raw !== undefined && raw !== null && raw !== '';

    let value = NOT_SUPPLIED;
    if (supplied) {
      if (field.kind === 'bool') value = raw ? 'Yes' : 'No';
      else if (field.kind === 'text') value = String(raw);
      else value = field.unit ? `${raw} ${field.unit}` : String(raw);
    }

    return {
      label: field.label,
      value,
      supplied,
      // An absent field has no origin to report, and printing one would
      // suggest somebody decided it.
      origin: supplied ? originOf(field.key, input, benchmark, field) : '—'
    };
  });
}

/**
 * Everything known about the band this run was measured against.
 * Returns null when the run was not compared to one — in which case the
 * paper must say that rather than print an empty section.
 */
export function benchmarkBasis(result) {
  const bench = result?.cost?.benchmark;
  const band = result?.cost?.band;
  if (!bench || !band) return null;

  return {
    corridor: bench.name,
    mode: result.input?.mode || null,
    low: band.low,
    median: band.median,
    high: band.high,
    currency: bench.currency || 'USD',
    provenance: band.provenance || null,
    basis: band.basis || null,
    payloadTonnes: band.payloadTonnes ?? null,
    /* measuredAt is when the underlying figures were struck; asOf is
       when someone last confirmed the band still held. Conflating them
       is how a 2023 tariff comes to look like this year's rate, which
       is exactly the error the vintage separation was landed to stop. */
    measuredAt: band.measuredAt ?? bench.measuredAt ?? null,
    checkedAt: bench.asOf ?? null
  };
}

/* A short, stable, human-transcribable reference.

   Serialised from the REGISTER, not from share.js. The first version
   hashed encodeRun(input), which was fine while only the corridor run
   produced packs and silently broken the moment financeability did:
   encodeRun walks the corridor field list, so a set of lender figures
   contains none of those fields, encodes to a string of empty
   separators, and every financeability document in existence would
   have carried the same reference. Serialising from the register the
   pack is already built on means the reference covers exactly the
   fields the paper prints, for any tool, by construction.

   FNV-1a: not a security hash and not used as one — nothing here
   resists a determined forger, and the reference is a filing aid, not
   a seal. The alphabet is base32-ish and excludes I, O, 0 and 1, so a
   reference read aloud in a meeting or copied off paper cannot be
   transcribed as a different one. */
const ALPHABET = '23456789ABCDEFGHJKLMNPQRSTUVWXYZ';

function canonical(input, register) {
  // Register order, absence marked explicitly. A field that is absent
  // and a field that is empty-string must not serialise alike, or two
  // materially different papers could share a reference.
  return register
    .map(({ key }) => {
      const v = input?.[key];
      if (v === undefined || v === null || v === '') return `${key}:∅`;
      return `${key}:${String(v)}`;
    })
    .join('|');
}

export function documentReference(input, prefix = 'KLA', register = CORRIDOR_RUN_REGISTER) {
  if (!input || typeof input !== 'object') return null;
  const encoded = canonical(input, register);
  // A form with nothing in it is not a document.
  if (register.every(({ key }) => input[key] === undefined || input[key] === null || input[key] === '')) {
    return null;
  }

  let hash = 0x811c9dc5;
  for (let i = 0; i < encoded.length; i += 1) {
    hash ^= encoded.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }

  let out = '';
  for (let i = 0; i < 8; i += 1) {
    out += ALPHABET[(hash >>> (i * 4)) & 31];
    if (i === 3) out += '-';
  }
  return `${prefix}-${out}`;
}

/**
 * What the paper must disclose about its own completeness.
 * A board paper that omits this invites a decision taken on the
 * assumption that everything relevant was to hand.
 */
export function completeness(result, register = CORRIDOR_RUN_REGISTER) {
  const rows = assumptionsRegister(result?.input || {}, result?.cost?.benchmark, register);
  const missing = rows.filter((r) => !r.supplied).map((r) => r.label);
  return {
    supplied: rows.length - missing.length,
    total: rows.length,
    missing,
    /* The engine's own list of what it withheld, which is a different
       and stronger statement than "this box was empty": it names the
       findings that were not computed as a result. */
    withheld: Array.isArray(result?.missing) ? result.missing : []
  };
}
